import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import AddIcon from '@mui/icons-material/Add';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardDoubleArrowDownRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowDownRounded';
import KeyboardDoubleArrowUpRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowUpRounded';
import LocalPrintshopRoundedIcon from '@mui/icons-material/LocalPrintshopRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import { Box, Button, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { FC, KeyboardEvent, MouseEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { DetailPanelMoveTarget } from 'src/components/dispatch/detailPanel/types';
import { isRowNewlyAssigned } from 'src/components/dispatch/newlyAssigned/isRowNewlyAssigned';
import { buildNoPlanBadgeRowIds } from 'src/components/dispatch/noPlanVehicle';
import { ShiftTruckRowInfo } from 'src/components/dispatch/shiftTruckRowInfo';
import { StopBulkMoveBar } from 'src/components/dispatch/StopBulkMoveBar';
import { resolveSwapTargets } from 'src/components/dispatch/swapTargets';
import { STICKY_HEADER_ATTR } from 'src/components/dispatch/useScrollToRow';
import {
  buildVehicleSortableGroups, truckOrderFromKeys, vehicleRowGroupKey,
} from 'src/components/dispatch/vehicleAssignment/vehicleSortableGroups';
import {
  VEHICLE_LIST_HEADER_ROW1_HEIGHT, VEHICLE_LIST_HEADER_ROW2_HEIGHT,
} from 'src/components/dispatch/vehicleCardStyles';
import {
  ALL_ROTATION_FILTERS, buildVehicleFilterLabel, hasGroupNarrowing, hasRotationNarrowing,
  RotationFilterValue, VehicleFilterMode,
} from 'src/components/dispatch/vehicleFilter';
import { LoadTotals } from 'src/components/dispatch/vehicleLoad';
import { ExpandedRowVM, ExpandedStopVM } from 'src/components/dispatch/vehicleRowExpand/buildExpandedRowVM';
import { reorderByKey } from 'src/components/dispatch/vehicleRowExpand/reorderByKey';
import TruckDisplayStatusMenu from 'src/components/TruckDisplayStatusMenu';
import { DispatchCard, Vehicle } from 'src/entities/dispatch';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';
import { useKeepArrayIdentity } from 'src/hooks/useKeepIdentity';

import { buildDisplayRotationNumbers, RotationRow, VehicleRowGroup } from './adapter';
import { VehicleTariffTotals } from './buildGrossProfitByTruckId';
import { DisplayMetric } from './DisplayMetricToggle';
import { VehicleListGroup, VehicleListRowState } from './VehicleListGroup';

// 静的な modifier。レンダリングのたびに新しい配列を生成しないようコンポーネント外で定義する。
const dndModifiers = [restrictToVerticalAxis];

// センサーの設定も同じ理由でコンポーネント外に置く。
// useSensor は useMemo の deps に options をそのまま並べる (@dnd-kit/core 6.3.1 の
// core.cjs.development.js:197-203) ため、インラインのオブジェクトリテラルを渡すと
// 毎描画で新しい sensor descriptor になり、useSensors の配列 (同 205-212) も別参照になる。
// その配列は DndContext の activators (useCombineActivators、同 1934-1945) の deps に入り、
// activators は internalContext の deps (同 3334-3349) に入る。つまり sensors の参照が
// 変わるだけで context 値が変わり、useSortable を呼ぶ全ての行が memo を無視して
// 描き直される（Task 6 の行の memo が実際に効いていなかった原因）。
const pointerSensorOptions = { activationConstraint: { distance: 8 } };

type BaseProps = {
  /** truck 並べ替え可否 (= 単日表示 startOn === endOn のとき true) */
  canSortTruckDisplayOrder: boolean;
  /** mutation 進行中フラグ。in-flight 中は次の drag をブロックする */
  isMutatingTruckOrder: boolean;
  /** drag 完了時に Presenter で楽観的更新 + サーバ保存を行うためのコールバック */
  onTruckOrderChange: (truckIds: number[]) => void;
  rowGroups: VehicleRowGroup[];
  isLoading: boolean;
  displayMetrics: Set<DisplayMetric>;
  /**
   * truckId → 粗利 (円)。呼び出し側の Presenter が算出して渡す。
   * optional にすると両画面の配線漏れが tsc もテストもすり抜けるため必須にしている。
   * 粗利を出さない画面は空 Map を渡す。
   */
  grossProfitByTruckId: ReadonlyMap<number, VehicleTariffTotals>;
  /**
   * 積載以外の制約違反。key は `RotationRow.rowId`。
   * 違反があった回転の行に出す（勤務単位にまとめると 2 回転目の違反が 1 回転目に並ぶ。
   * buildConstraintAlertsByRowId 参照）。勤務時間の超過だけは勤務そのものの違反なので、
   * 同関数が先頭の回転の rowId にだけ積む。
   */
  constraintAlertsByRowId?: ReadonlyMap<string, readonly string[]>;
  /**
   * 回転行ごとの積載量（= 同時に積んでいる量の最大。key = RotationRow.rowId）。
   * 勤務全体の operation を走査しないと求まらないので、絞り込み前の全グループから
   * 親が buildPeakLoadByRowId で 1 度だけ作って渡す。
   */
  peakLoadByRowId: ReadonlyMap<string, LoadTotals>;
  /**
   * 自動配車で新しく案件が入った勤務。shift_truck.id → 案件 ID。
   * 車両単位にすると、1 台に複数のドライバーが乗る勤務で同じ車両の全行が光る。
   * /map だけが渡す（カード配車は渡さないので現状の見た目のまま）
   */
  newlyAssignedByShiftTruck?: ReadonlyMap<number, ReadonlySet<number>>;
  selectedRowIds: Set<string>;
  allSelected: boolean;
  /** 車両リストの絞り込み（配車状況・回転・選択中）。1 つだけ選べる */
  truckDisplayStatus: VehicleFilterMode;
  onTruckDisplayStatusChange: (mode: VehicleFilterMode) => void;
  /** チェック済みの回転。既定は全部（絞っていない状態） */
  rotations: readonly RotationFilterValue[];
  onRotationsChange: (next: RotationFilterValue[]) => void;
  /** 制約違反のある車両だけに絞るか。既定はチェックなし */
  constraintViolationOnly: boolean;
  onConstraintViolationOnlyChange: (next: boolean) => void;
  hasSelection: boolean;
  /** id=0「すべて表示する」を先頭に含むグループ全体。空ならグループ UI を出さない */
  truckGroupEntitiesWithAll: PlanningsGroupEntity[];
  /**
   * driverId → ドライバー名。1 台に複数のドライバーが乗る勤務で行ごとの名前を出すのに使う。
   * 未指定なら従来どおり車両単位の Vehicle.driverName を出す。
   */
  driverNameById?: ReadonlyMap<number, string>;
  /**
   * shift_truck.id → 勤務時間・車番。ドライバー名と同じく、1 台に複数のドライバーが
   * 乗る勤務では車両単位の代表値が使えない。未指定なら従来どおり車両単位の値を出す。
   */
  shiftTruckRowInfoById?: ReadonlyMap<number, ShiftTruckRowInfo>;
  /**
   * 表示中の日 ('YYYY-MM-DD')。この日と違う日に始まる勤務の勤務時間に日付を付ける。
   * 未指定なら日をまたぐ勤務にだけ付ける。
   */
  baseDay?: string;
  /** 現在チェック済みのグループ index 群 */
  selectedTruckGroupIndices: number[];
  onSelectedTruckGroupIndicesChange: (next: number[]) => void;
  onRowToggle: (rowId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  /** 選択中の行の案件をまとめて未割当に戻す。未指定ならボタン非表示（readOnly とは独立） */
  onUnassignSelected?: () => void;
  /**
   * 選択中の 2 車両の配送計画を入れ替える（V3Plans の swapRequest 相当）。
   * 未指定ならボタン非表示。別車両 2 台を選んだときだけ出る。
   */
  onSwapSelected?: (fromDeliveryId: number, toDeliveryId: number) => void;
  /** 未指定ならボタン非表示（readOnly とは独立） */
  onAddVehicle?: () => void;
  /** 行の DOM を rowId で登録する。地図から選んだ行までスクロールするのに使う */
  registerRow?: (rowId: string, element: HTMLElement | null) => void;
  /**
   * 「計画無」バッジの × 。未指定なら × を出さない。
   * shiftTruckId はその行の勤務。1 台に複数ドライバーが乗る勤務で対象を決めるのに使う
   */
  /** 勤務ごと削除する。対象は押したカードの勤務 (shift_truck.id) */
  onDeleteShift?: (shiftTruckId: number | null) => void;
  /** 指定時は × を disabled にし、素の title 属性で理由を出す */
  deleteShiftDisabledReason?: string;
  /** 回転は勤務 (delivery) の中の概念。追加先・削除先は勤務で決める */
  onAddRotation: (deliveryId: number) => void;
  onDeleteRotation: (deliveryId: number, rotationNumber: number) => void;
  /** 指定時は回転削除ボタンを disabled にし、素の title 属性で理由を出す（Task 10 #2。VehicleInfoView への中継）。 */
  deleteRoundDisabledReason?: string;
  /**
   * 車両名 / ドライバー名 / 車番 クリック時のハンドラ。Presenter 側でメニューを開いて
   * mutation を発火する。対象は勤務 (shift_truck.id)。1 台に複数の勤務が乗るので
   * truckId では対象を決められない。
   */
  onTruckEdit?: (shiftTruckId: number | null, anchor: HTMLElement) => void;
  onDriverEdit?: (shiftTruckId: number | null, anchor: HTMLElement) => void;
  onPlateNumberEdit?: (shiftTruckId: number | null, anchor: HTMLElement) => void;
  /** 帳票出力 (V3Plans の SelectPrintPresenter と同じダイアログ) を開く */
  onOpenPrint?: () => void;
  /**
   * 配車指示メールを送る。未指定ならボタンを出さない
   * （ドライバーアプリのライセンスが無い会社では handler ごと渡さない）。
   */
  onSendOperationMail?: () => void;
  /**
   * 配送状況の文言（「N分前に更新あり」/「配送状況 MM/dd HH:mm」）。
   * 未指定なら配送状況を出さない。
   */
  operationStatusText?: string;
  /**
   * 配送状況の再取得。**新しい更新を見つけたときだけ**渡す。
   * 平時に押す用の無いボタンを常駐させない（旧地図配車と同じ規則）。
   */
  onRefreshOperationStatuses?: () => void;
  /** 指定時はメールボタンを disabled にし、素の title 属性で理由を出す */
  sendOperationMailDisabledReason?: string;
  /** false で回転追加/削除ボタンを隠す。default true（convenience 現状維持）。readOnly とは独立。 */
  rotationEditable?: boolean;
  /** 確定済み shift_truck(delivery) id 集合。各行の確定バッジ表示に使う。readOnly とは独立。 */
  fixedShiftTruckIds?: Set<number>;
  /** 「配車確定/確定解除」ボタン押下。未指定ならボタン非表示。readOnly とは独立。 */
  onToggleFixedSelected?: () => void;
  /** 確定ボタンのラベル。undefined のときボタン非表示（有効な確定対象が 0 件のとき Presenter が undefined を渡す）。 */
  fixedToggleLabel?: string;
  /** 確定 mutation 実行中フラグ。 */
  isTogglingFixed?: boolean;
  /** 指定時は確定ボタンを disabled にし、素の title 属性で理由を出す（Task 10 #1）。 */
  fixedToggleDisabledReason?: string;
  /** truckId から車両カラー (hex) を引く。undefined 返却 or prop 未指定なら色バー非表示。 */
  vehicleColorFor?: (truckId: number) => string | undefined;
  /** 色バー クリック時のハンドラ。Presenter 側で色ピッカーを開く。 */
  onVehicleColorClick?: (truckId: number, anchor: HTMLElement) => void;
  /** 指定時は色バーを disabled にし、素の title 属性で理由を出す（Task 10 #7。VehicleInfoView への中継）。 */
  colorDisabledReason?: string;
  /**
   * 停車地行のクリック。未指定なら展開行はクリックできない(P2b の onShowDetail と同じ規則)。
   * 展開が有効でも任意なので判別共用体には入れない。
   */
  onSelectStop?: (card: DispatchCard, vehicle: Vehicle, dispatchOrderIndex: number, stopId: string) => void;
  /**
   * 地点の複数選択と一括割付 (logposeTech/log_web_issues#325)。
   *
   * Why 1 つのオブジェクトにまとめる: 独立した optional prop を並べると、片方だけ渡した
   * 配線漏れを型で防げない（下の RowExpandProps を判別共用体にしているのと同じ意図）。
   * 未指定ならバーも選択表示も出ない（カード配車）。
   */
  stopBulkMove?: {
    /** rowId → その行で選択できる stopId。index 由来なので選択では変わらない */
    selectableStopIdsByRowId: ReadonlyMap<string, ReadonlySet<string>>;
    /** rowId → 選択中の stopId。選択のある行だけ入る */
    selectedStopIdsByRowId: ReadonlyMap<string, ReadonlySet<string>>;
    onClear: () => void;
    moveTargets: readonly DetailPanelMoveTarget[];
    onMove: (targetRowId: string) => void;
    /** 自動配車の実行中など。移動だけ止め、選択解除は残す */
    disabledReason?: string;
  };
  /**
   * 回転を並べ替えたときに呼ぶ。未指定なら並べ替えできない（ハンドルも出さない）。
   * 渡すのは同じ車両の「動かした回転」と「落とした先の回転」の rotationNumber。
   */
  /** 回転は勤務 (delivery) の中の並び。対象は掴んだカードの勤務 (shift_truck.id) */
  onReorderRotations?: (shiftTruckId: number | null, fromRotationNumber: number, toRotationNumber: number) => void;
  /**
   * 展開行の停車地を並べ替えたときに呼ぶ。未指定なら並べ替えできない。
   * 渡すのは並べ替え後の停車地全体で、保存する id 列の組み立ては呼び出し側が行う。
   */
  onReorderStops?: (row: RotationRow, stops: ExpandedStopVM[]) => void;
  /** 指定時は並べ替えハンドルを disabled にし、素の title 属性で理由を出す */
  reorderDisabledReason?: string;
  /**
   * GA の screen_name。**必須**。既定値を持たせると誤ラベルの GA が静かに飛ぶ
   * (P2b で全境界について必須と決めた契約)。ConvenienceDispatchUnassignedList:102 と同じ prop 名。
   */
  screenName: string;
};

/**
 * rowExpandable が true のとき expandedRowIds / onToggleRowExpanded / expandedRowVMByRowId が
 * 必須という関係を型に落とす。false 側にも同名を never で置くのは、このコンポーネントが
 * 引数で props を一括分割代入しているため。false 側に同名が無い共用体だと分割代入の時点で
 * TS2339 になる(実測確認済み)。never を置いても rowExpandable だけ渡した配線漏れは TS2322 で検出できる。
 */
type RowExpandProps =
  | {
      rowExpandable?: false;
      expandedRowIds?: never;
      onToggleRowExpanded?: never;
      onToggleAllExpanded?: never;
      allRowsExpanded?: never;
      expandedRowVMByRowId?: never;
    }
  | {
      rowExpandable: true;
      expandedRowIds: ReadonlySet<string>;
      onToggleRowExpanded: (rowId: string) => void;
      /** ヘッダの「すべて展開 / すべて閉じる」。対象行の解決は Presenter 側 */
      onToggleAllExpanded: () => void;
      /** 表示中の行がすべて展開済みか。ボタンの向き（展開 / 閉じる）を決める */
      allRowsExpanded: boolean;
      /** rowId → 展開行 VM。地図マーカーと同じ番号にするため Presenter が 1 回だけ作った Map を渡す */
      expandedRowVMByRowId: ReadonlyMap<string, ExpandedRowVM>;
    };

type Props = BaseProps & RowExpandProps;

const ConvenienceDispatchVehicleList: FC<Props> = memo(({
  canSortTruckDisplayOrder,
  isMutatingTruckOrder,
  onTruckOrderChange,
  rowGroups,
  isLoading,
  displayMetrics,
  grossProfitByTruckId,
  constraintAlertsByRowId,
  peakLoadByRowId,
  newlyAssignedByShiftTruck,
  selectedRowIds,
  allSelected,
  truckDisplayStatus,
  onTruckDisplayStatusChange,
  rotations,
  onRotationsChange,
  constraintViolationOnly,
  onConstraintViolationOnlyChange,
  hasSelection,
  truckGroupEntitiesWithAll,
  driverNameById,
  shiftTruckRowInfoById,
  baseDay,
  selectedTruckGroupIndices,
  onSelectedTruckGroupIndicesChange,
  onRowToggle,
  onSelectAll,
  onDeselectAll,
  onUnassignSelected,
  onSwapSelected,
  onAddVehicle,
  registerRow,
  onDeleteShift,
  deleteShiftDisabledReason,
  onAddRotation,
  onDeleteRotation,
  deleteRoundDisabledReason,
  onTruckEdit,
  onDriverEdit,
  onPlateNumberEdit,
  onOpenPrint,
  onSendOperationMail,
  sendOperationMailDisabledReason,
  operationStatusText,
  onRefreshOperationStatuses,
  rotationEditable = true,
  fixedShiftTruckIds,
  onToggleFixedSelected,
  fixedToggleLabel,
  isTogglingFixed = false,
  fixedToggleDisabledReason,
  vehicleColorFor,
  onVehicleColorClick,
  colorDisabledReason,
  rowExpandable = false,
  expandedRowIds,
  onToggleRowExpanded,
  onToggleAllExpanded,
  allRowsExpanded = false,
  expandedRowVMByRowId,
  onSelectStop,
  stopBulkMove,
  onReorderRotations,
  onReorderStops,
  reorderDisabledReason,
  screenName,
}) => {
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);

  // 車両表示順の DnD。実際の楽観的更新 + サーバ保存は Presenter の onTruckOrderChange に委譲する
  // (Presenter 側で local state を持ち、ボード描画にも反映するため)。
  const sensors = useSensors(useSensor(PointerSensor, pointerSensorOptions));

  // 回転を並べ替えたときの見た目。保存 (debounce 2 秒) が返るまでこちらを描く。
  // vehicle.id → 並べ替え後の行。rowGroups が入れ替わったらサーバの並びに戻す
  const [rotationPreview, setRotationPreview] = useState<ReadonlyMap<string, RotationRow[]>>(new Map());
  useEffect(() => setRotationPreview(new Map()), [rowGroups]);

  // 並べ替えの識別子は勤務単位。グループは勤務ごとに分かれるので、truckId を使うと
  // 1 台に 2 つの勤務が乗るとき React key と dnd-kit の id が重複する
  const sortableGroups = useMemo(() => buildVehicleSortableGroups(rowGroups), [rowGroups]);
  // SortableContext の items は参照で依存比較される。グループの並びが変わっていないのに
  // 新しい配列を渡すと context の値が毎描画で変わり、配下の行がまるごと描き直される
  // （useKeepArrayIdentity のコメント参照）
  const keepSortableKeys = useKeepArrayIdentity<string>();
  const sortableKeys = useMemo(
    () => keepSortableKeys(sortableGroups.map((g) => g.key)),
    [sortableGroups, keepSortableKeys],
  );

  // 入れ替えの可否と対象は resolveSwapTargets に閉じ込める。
  // null なら条件を満たさないのでボタンごと出さない
  const swapTargets = useMemo(
    () => resolveSwapTargets(selectedRowIds, rowGroups),
    [selectedRowIds, rowGroups],
  );

  const handleVehicleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (!canSortTruckDisplayOrder || isMutatingTruckOrder) return;

    const oldIndex = sortableKeys.indexOf(String(active.id));
    const newIndex = sortableKeys.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;

    // 保存する並び順は車両単位。同じ車両の勤務が 2 つ並ぶので重複を落とす
    onTruckOrderChange(truckOrderFromKeys(sortableGroups, arrayMove([...sortableKeys], oldIndex, newIndex)));
  }, [canSortTruckDisplayOrder, isMutatingTruckOrder, onTruckOrderChange, sortableGroups, sortableKeys]);

  // 回転の並べ替え。全グループで 1 つの参照を使い回すので、どのグループの操作かは
  // 引数で受ける（グループごとに useCallback を作るとグループの memo が毎回破れる）。
  //
  // Why not truckId == null も見る: 回転の DndContext は truckId != null の
  // グループにしか無い（VehicleListGroup の rotationSortable）
  const handleRotationDragEnd = useCallback((
    groupKey: string,
    shiftTruckId: number | null,
    rows: RotationRow[],
    event: DragEndEvent,
  ) => {
    const next = reorderByKey(rows, (it) => it.rowId, event.active.id, event.over?.id);
    if (!next || !onReorderRotations) return;
    const from = rows.find((it) => it.rowId === event.active.id);
    const to = rows.find((it) => it.rowId === event.over?.id);
    setRotationPreview((prev) => new Map(prev).set(groupKey, next));
    onReorderRotations(shiftTruckId, from.rotationNumber, to.rotationNumber);
  }, [onReorderRotations]);

  // 行へ素通しする prop 群。参照を固定しないと全グループの memo が毎描画で破れる
  const rowProps = useMemo(() => ({
    // expandable はグループに依らない boolean なのでここに入れる
    expandable: !!rowExpandable,
    displayMetrics,
    rotationEditable,
    reorderDisabledReason,
    deleteShiftDisabledReason,
    deleteRoundDisabledReason,
    colorDisabledReason,
    screenName,
    baseDay,
    onRowToggle,
    onToggleRowExpanded,
    onAddRotation,
    onDeleteRotation,
    onDeleteShift,
    onTruckEdit,
    onDriverEdit,
    onPlateNumberEdit,
    onVehicleColorClick,
    onSelectStop,
    onReorderStops,
    registerRow,
  }), [rowExpandable, displayMetrics, rotationEditable, reorderDisabledReason,
    deleteShiftDisabledReason, deleteRoundDisabledReason, colorDisabledReason, screenName, baseDay,
    onRowToggle, onToggleRowExpanded, onAddRotation, onDeleteRotation, onDeleteShift,
    onTruckEdit, onDriverEdit, onPlateNumberEdit, onVehicleColorClick,
    onSelectStop, onReorderStops, registerRow]);

  // グループごと・行ごとの表示状態。Set / Map をグループへ素通しすると、どれか 1 つの
  // 参照が変わるだけで全グループが描き直されるので、ここで解決して配列で渡す
  const rowStatesByGroup = useMemo(
    () => rowGroups.map((group) => {
      const rows = rotationPreview.get(vehicleRowGroupKey(group)) ?? group.rows;
      // 回転バッジの番号。並べ替えプレビュー中の並びで数える
      const displayRotationNumbers = buildDisplayRotationNumbers(rows);
      // 「計画無」は勤務 (shift_truck) 単位。× が勤務ごと消す操作なので回転単位では判定せず、
      // かつ車両単位にもしない（1 台に複数のドライバーが乗る勤務では、配車のある人と
      // 無い人が同じ車両に並ぶ）
      const noPlanBadgeRowIds = buildNoPlanBadgeRowIds(rows);
      return rows.map((row, rowIndex): VehicleListRowState => {
        // 空回転(再積み込み拠点だけの placeholder 行)は展開しても中身が無い。
        // トグルは全行に出す（無いと行頭が揃わない）が、中身が無ければ
        // パネルは描かずアイコンだけ変える（サンプルと同じ）
        const expanded = !!rowExpandable && !!expandedRowIds?.has(row.rowId);
        // 「回転を追加」で足した行は shift_truck を持たないので追加元の勤務を引く。
        // 車両単位の代表値に落とすと、夜間便が連続する車両で別の日の勤務が出る
        const rowShiftTruckId = row.shiftTruckId ?? row.source.extraOfDeliveryId;
        return {
          selected: selectedRowIds.has(row.rowId),
          expanded,
          // 差分は勤務 (shift_truck) 単位で来る。そのまま使うと 2 回転目に
          // 案件が入っただけで同じ勤務の 1 回転目まで光るので、行が持つ案件と
          // 突き合わせる (isRowNewlyAssigned)
          newlyAssigned: isRowNewlyAssigned(row, newlyAssignedByShiftTruck),
          fixed: !!fixedShiftTruckIds
            && !row.source.isPlaceholderRow
            && fixedShiftTruckIds.has(row.source.deliveryId),
          constraintAlerts: constraintAlertsByRowId?.get(row.rowId),
          // VM は Presenter が rowId ごとに 1 回だけ組んだものを引くだけ(地図マーカーと
          // 番号を一致させるため、ここでは組み立てない。buildExpandedRowVMByRowId 参照)。
          expandedVM: expanded ? expandedRowVMByRowId?.get(row.rowId) ?? null : null,
          // 1 台に複数のドライバーが同じ時間帯で乗る勤務があるので、車両単位の
          // 代表 1 人ではなく行の乗務者・勤務時間・車番を引く
          driverName: row.driverId == null ? undefined : driverNameById?.get(row.driverId),
          shiftTruckRowInfo: rowShiftTruckId == null
            ? undefined : shiftTruckRowInfoById?.get(rowShiftTruckId),
          barColor: vehicleColorFor?.(row.truckId),
          // バッジ表示は同じ勤務 (shift_truck) の中での行順 (1..N) で連番にする
          // (理由は VehicleListRow の rotationNumber 側のコメント)
          displayRotationNumber: displayRotationNumbers[rowIndex],
          noPlan: noPlanBadgeRowIds.has(row.rowId),
          // 回転の追加・削除の対象になる勤務。「回転を追加」で足した行は
          // deliveryId が負のダミーなので、追加元 (extraOfDeliveryId) を使う
          rowDeliveryId: row.source.extraOfDeliveryId ?? row.source.deliveryId,
          newlyAssignedOrderIds: row.shiftTruckId == null
            ? undefined : newlyAssignedByShiftTruck?.get(row.shiftTruckId),
          // 選択可能性と選択状態は別々に配る。空 Set を全行へフォールバックさせると
          // 選択できない地点まで aria-pressed が付き、参照も毎回変わって memo が破れる
          selectableStopIds: stopBulkMove?.selectableStopIdsByRowId.get(row.rowId),
          selectedStopIds: stopBulkMove?.selectedStopIdsByRowId.get(row.rowId),
        };
      });
    }),
    [rowGroups, rotationPreview, selectedRowIds, rowExpandable, expandedRowIds,
      newlyAssignedByShiftTruck, fixedShiftTruckIds, constraintAlertsByRowId,
      expandedRowVMByRowId, driverNameById, shiftTruckRowInfoById, vehicleColorFor,
      stopBulkMove],
  );

  // バーに出す選択件数。行ごとの Set を合算する（選択は 1 車両に閉じているので高々 1 行ぶん）
  const selectedStopCount = useMemo(
    () => [...(stopBulkMove?.selectedStopIdsByRowId.values() ?? [])]
      .reduce((total, ids) => total + ids.size, 0),
    [stopBulkMove],
  );

  // フィルタボタンのラベル: ステータスが「すべて表示」以外 > 部分選択中のグループ名 > "すべて"
  const isFiltered = truckDisplayStatus !== 'すべて表示'
    || hasGroupNarrowing(selectedTruckGroupIndices)
    || hasRotationNarrowing(rotations)
    || constraintViolationOnly;
  const filterLabel = buildVehicleFilterLabel(
    truckDisplayStatus,
    selectedTruckGroupIndices,
    truckGroupEntitiesWithAll,
    { rotations, constraintViolationOnly },
  );
  // × もメニューの「フィルターをクリア」と同じく全条件を既定へ戻す
  const clearFilters = () => {
    onTruckDisplayStatusChange('すべて表示');
    onSelectedTruckGroupIndicesChange(truckGroupEntitiesWithAll.map((_g, i) => i));
    onRotationsChange([...ALL_ROTATION_FILTERS]);
    onConstraintViolationOnlyChange(false);
  };

  if (isLoading) {
    return (
      <Stack spacing={1} sx={{ p: 1 }}>
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} variant="rectangular" height={72} />
        ))}
      </Stack>
    );
  }

  return (
    <Box>
      {/* 固定ヘッダ (空状態でも常に表示) */}
      <Box
        // 地図から車両を選んだときのスクロール位置計算がこの目印でヘッダ高さを測る
        // (useScrollToRow.ts)。外すと選んだ行がヘッダの裏に潜る
        {...{ [STICKY_HEADER_ATTR]: '' }}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 30,
          borderBottom: 1,
          borderColor: 'divider',
          // 不透明にする。半透明だとスクロール時に下の車両カードが透けて、
          // フィルターや全選択が読みにくくなる
          bgcolor: 'hsl(var(--secondary))',
        }}
      >
        {/* Row 1: フィルタ + 車両追加。高さはレーンの列番号ヘッダと共有する定数から取る */}
        <Box sx={{ display: 'flex', height: VEHICLE_LIST_HEADER_ROW1_HEIGHT, alignItems: 'center', justifyContent: 'space-between', px: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {/* 絞り込み中は塗りにして、× をその場に出す。絞り込んでいることに気づかず
                「台数が合わない」と誤解されるのを防ぐ（サンプル準拠） */}
            <Button
              ref={filterButtonRef}
              size="small"
              variant={isFiltered ? 'contained' : 'outlined'}
              color={isFiltered ? 'primary' : 'inherit'}
              startIcon={<FilterListIcon sx={{ fontSize: 12 }} />}
              endIcon={isFiltered ? (
                <Box
                  component="span"
                  role="button"
                  tabIndex={0}
                  aria-label="フィルターをクリア"
                  onClick={(e: MouseEvent<HTMLElement>) => {
                    // 親の onClick（メニューを開く）を止める
                    e.stopPropagation();
                    clearFilters();
                  }}
                  onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    e.stopPropagation();
                    clearFilters();
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    fontSize: 11,
                    lineHeight: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' },
                  }}
                >
                  ×
                </Box>
              ) : <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />}
              onClick={() => {
                ReactGA.event('click', {
                  screen_name: screenName,
                  button_name: '車両表示ステータス フィルタ',
                });
                setFilterMenuOpen(true);
              }}
              sx={{ height: 28, px: 1, fontSize: 12, textTransform: 'none', minWidth: 0 }}
            >
              {filterLabel}
            </Button>
            <TruckDisplayStatusMenu
              anchorEl={filterButtonRef.current}
              open={filterMenuOpen}
              onClose={() => setFilterMenuOpen(false)}
              mode={truckDisplayStatus}
              onModeChange={(next) => {
                ReactGA.event('select', {
                  screen_name: screenName,
                  button_name: '車両表示ステータス',
                  label: next,
                });
                onTruckDisplayStatusChange(next);
              }}
              hasSelection={hasSelection}
              groupEntities={truckGroupEntitiesWithAll}
              selectedGroupIndices={selectedTruckGroupIndices}
              onSelectedGroupIndicesChange={(next) => {
                ReactGA.event('select', {
                  screen_name: screenName,
                  button_name: '車両グループ選択',
                  label: next.join(','),
                });
                onSelectedTruckGroupIndicesChange(next);
              }}
              rotations={rotations}
              onRotationsChange={(next) => {
                ReactGA.event('select', {
                  screen_name: screenName,
                  button_name: '回転で絞込',
                  label: next.join(','),
                });
                onRotationsChange(next);
              }}
              constraintViolationOnly={constraintViolationOnly}
              onConstraintViolationOnlyChange={(next) => {
                ReactGA.event('select', {
                  screen_name: screenName,
                  button_name: '制約違反のある車両のみ',
                  label: next ? 'ON' : 'OFF',
                });
                onConstraintViolationOnlyChange(next);
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* readOnly ではなく handler の有無で決める（配車リセット・確定ボタンと同じ分離） */}
            {onAddVehicle && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  ReactGA.event('click', {
                    screen_name: screenName,
                    button_name: '車両追加',
                  });
                  onAddVehicle();
                }}
                sx={{ height: 28, px: 1, fontSize: 12, textTransform: 'none', minWidth: 0, whiteSpace: 'nowrap' }}
              >
                車両追加
              </Button>
            )}
            {/* 配送状況。文言は常に出し、再取得ボタンは新しい更新があるときだけ */}
            {operationStatusText && (
              <Typography
                variant="caption"
                sx={{ fontSize: 10, color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                {operationStatusText}
              </Typography>
            )}
            {onRefreshOperationStatuses && (
              <Tooltip title="配送状況を再取得" arrow>
                <IconButton
                  size="small"
                  onClick={() => {
                    ReactGA.event('click', {
                      screen_name: screenName,
                      button_name: '配送状況 再取得',
                    });
                    onRefreshOperationStatuses();
                  }}
                  aria-label="配送状況を再取得"
                  sx={{ p: 0.5, color: 'text.primary' }}
                >
                  <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {/* 帳票出力の左。送る対象＝選択した車両がこのリストにあるので、
                地図のツールバーではなくここに置く */}
            {onSendOperationMail && (
              <Tooltip title={sendOperationMailDisabledReason || '配車指示メールを送る'} arrow>
                {/* disabled な button は hover を拾わないので span で包む(MUI の作法) */}
                <span>
                  <IconButton
                    size="small"
                    disabled={!!sendOperationMailDisabledReason}
                    title={sendOperationMailDisabledReason}
                    onClick={() => {
                      ReactGA.event('click', {
                        screen_name: screenName,
                        button_name: '配車指示メール',
                      });
                      onSendOperationMail();
                    }}
                    aria-label="配車指示メールを送る"
                    sx={{ p: 0.5, color: 'text.primary' }}
                  >
                    <MailOutlineRoundedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {onOpenPrint && (
              <Tooltip title="配送指示書を出力する" arrow>
                <IconButton
                  size="small"
                  onClick={() => {
                    ReactGA.event('click', {
                      screen_name: screenName,
                      button_name: '帳票出力',
                    });
                    onOpenPrint();
                  }}
                  aria-label="帳票出力"
                  sx={{ p: 0.5, color: 'text.primary' }}
                >
                  <LocalPrintshopRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Row 2: すべて展開 + 全選択 + 選択件数 */}
        <Box sx={{ display: 'flex', height: VEHICLE_LIST_HEADER_ROW2_HEIGHT, alignItems: 'center', gap: 0.75, borderTop: '1px solid hsl(var(--border) / 0.4)', px: 1.5 }}>
          {rowExpandable && onToggleAllExpanded && (
            <Tooltip title={allRowsExpanded ? 'すべて閉じる' : 'すべて展開'} placement="top" arrow>
              <IconButton
                size="small"
                aria-label={allRowsExpanded ? 'すべて閉じる' : 'すべて展開'}
                onClick={() => {
                  ReactGA.event('click', {
                    screen_name: screenName,
                    button_name: '車両 すべて展開',
                    label: allRowsExpanded ? 'OFF' : 'ON',
                  });
                  onToggleAllExpanded();
                }}
                sx={{ width: 24, height: 24 }}
              >
                {allRowsExpanded
                  ? <KeyboardDoubleArrowUpRoundedIcon sx={{ fontSize: 16, color: 'text.primary' }} />
                  : <KeyboardDoubleArrowDownRoundedIcon sx={{ fontSize: 16, color: 'text.primary' }} />}
              </IconButton>
            </Tooltip>
          )}
          <Button
            size="small"
            variant="text"
            startIcon={allSelected
              ? <CheckBoxOutlinedIcon sx={{ fontSize: 12, color: 'primary.main' }} />
              : <CheckBoxOutlineBlankOutlinedIcon sx={{ fontSize: 12 }} />}
            onClick={() => {
              ReactGA.event('click', {
                screen_name: screenName,
                button_name: '車両 全選択',
                label: allSelected ? 'OFF' : 'ON',
              });
              if (allSelected) onDeselectAll(); else onSelectAll();
            }}
            sx={{ height: 24, px: 0.75, fontSize: 10, textTransform: 'none', minWidth: 0, color: 'text.primary' }}
          >
            {allSelected ? '全解除' : '全選択'}
          </Button>
          {selectedRowIds.size > 0 && (
            <>
              <Box
                component="span"
                sx={{
                  borderRadius: '9999px',
                  bgcolor: 'hsl(var(--primary) / 0.1)',
                  px: 1,
                  py: '2px',
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'hsl(var(--primary))',
                }}
              >
                {selectedRowIds.size}件選択中
              </Box>
              {/* readOnly ではなく handler の有無で決める。/map は readOnly だが
                  配車リセットは使える（確定ボタンと同じ分離の仕方） */}
              {onUnassignSelected && (
                <>
                  <Box sx={{ mx: 0.5, height: 16, width: '1px', bgcolor: 'hsl(var(--border))' }} />
                  {/* 幅の限られたツールバーではラベルが場所を食うので、サンプル
                      (MapPage.tsx:1362-1375) と同じくアイコンだけにする */}
                  <Tooltip title="選択した車両の配送をすべて未割当に戻す" placement="top" arrow>
                    <IconButton
                      size="small"
                      aria-label="選択した車両の配送を未割当に戻す"
                      onClick={() => {
                        ReactGA.event('click', {
                          screen_name: screenName,
                          button_name: '未割当に戻す',
                        });
                        onUnassignSelected();
                      }}
                      sx={{ width: 24, height: 24 }}
                    >
                      <RestartAltRoundedIcon sx={{ fontSize: 14, color: 'error.main' }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {/* 入れ替えは別車両 2 台のときだけ。条件は resolveSwapTargets に閉じ込め、
                  対象を解決できないときはボタンごと出さない（同 :1376-1399） */}
              {onSwapSelected && swapTargets && (
                <Tooltip title="選択した2車両の配送計画を入れ替える" placement="top" arrow>
                  <IconButton
                    size="small"
                    aria-label="選択した2車両の配送を入れ替える"
                    onClick={() => {
                      ReactGA.event('click', {
                        screen_name: screenName,
                        button_name: '配送計画の入れ替え',
                      });
                      onSwapSelected(swapTargets[0], swapTargets[1]);
                    }}
                    sx={{ width: 24, height: 24 }}
                  >
                    <SwapHorizRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
          {/* ここから右。全選択〜入れ替えは左に固め、確定だけ右へ逃がす
              (サンプル MapPage.tsx:1400) */}
          <Box data-testid="toolbar-spacer" sx={{ flex: 1 }} />
          {onToggleFixedSelected && fixedToggleLabel && (
            <Button
              size="small"
              variant="outlined"
              disabled={isTogglingFixed || !!fixedToggleDisabledReason}
              title={fixedToggleDisabledReason}
              onClick={() => { onToggleFixedSelected(); }}
              sx={{ height: 24, px: 1, fontSize: 11, textTransform: 'none', minWidth: 0, whiteSpace: 'nowrap' }}
            >
              {fixedToggleLabel}
            </Button>
          )}
        </Box>

        {/* 地点の一括割付バー。ヘッダの中に置く（外に出すとスクロールで流れ、
            useScrollToRow.ts:51 のヘッダ高さ実測からも外れて行がヘッダの裏に潜る） */}
        {stopBulkMove && (
          <StopBulkMoveBar
            count={selectedStopCount}
            moveTargets={stopBulkMove.moveTargets}
            onMove={stopBulkMove.onMove}
            onClear={stopBulkMove.onClear}
            disabledReason={stopBulkMove.disabledReason}
            screenName={screenName}
          />
        )}
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleVehicleDragEnd}
        modifiers={dndModifiers}
      >
        <SortableContext items={sortableKeys} strategy={verticalListSortingStrategy}>
          {rowGroups.map((group, groupIndex) => {
            // 並べ替えの識別子。回転並べ替えプレビューのキーも同じグループ識別子
            // (vehicleRowGroupKey) なので、sortableGroups の key を使い回す
            const { key: sortableKey, truckId } = sortableGroups[groupIndex];
            // 保存が返るまでの見た目。debounce で 2 秒待つので、これが無いと掴んだ行が元に戻る
            const rows = rotationPreview.get(sortableKey) ?? group.rows;
            // 勤務 (delivery) ごとにグループが分かれるので、同じ車両のグループが複数並ぶ。
            // 粗利は車両合計なので最初のグループにだけ出す（全部に出すと同じ金額が
            // 何度も並び、勤務ごとの値と誤読される）
            const isFirstGroupOfTruck = rowGroups.findIndex((g) => g.vehicle.id === group.vehicle.id) === groupIndex;
            return (
              <VehicleListGroup
                key={`${sortableKey}-info`}
                group={group}
                rows={rows}
                sortableKey={sortableKey}
                truckId={truckId}
                isLastGroup={groupIndex === rowGroups.length - 1}
                vehicleSortDisabled={!canSortTruckDisplayOrder || isMutatingTruckOrder}
                onRotationDragEnd={onReorderRotations ? handleRotationDragEnd : undefined}
                sensors={sensors}
                rowProps={rowProps}
                peakLoadByRowId={peakLoadByRowId}
                rowStates={rowStatesByGroup[groupIndex]}
                tariffTotals={isFirstGroupOfTruck
                  ? grossProfitByTruckId.get(rows[0]?.truckId) ?? null : null}
              />
            );
          })}
        </SortableContext>
      </DndContext>
    </Box>
  );
});

export default ConvenienceDispatchVehicleList;
